import { useMemo, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  RichTextContent,
  useCollectionBinding,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

// ============================================================================
// Types
// ============================================================================

interface FAQItem {
  question?: string | null;
  answer?: string | null;
  category?: string | null;
  [key: string]: unknown;
}

interface FAQGroup {
  category: string;
  items: Array<FAQItem & { originalIndex: number }>;
}

const UNCATEGORIZED = 'General';

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}

function hasValue(val: unknown): val is string {
  if (val == null) return false;
  const s = String(val).trim().toLowerCase();
  return s !== '' && s !== 'false' && s !== 'null' && s !== 'undefined';
}

const FLOOR_PLANS_HREF = '/floor-plans';
const OCCUPANCY_QUALIFICATIONS_HREF =
  'https://www.pricebrotherskc.com/ResidentialRentalQualifications/';

/**
 * Rewrite text nodes outside existing anchors so FAQ copy can deep-link
 * without requiring CMS edits to every property's faqs collection.
 */
function mapTextOutsideAnchors(
  html: string,
  transform: (text: string) => string,
): string {
  let inAnchor = 0;
  return html.replace(
    /(<a\b[^>]*>)|(<\/a>)|(<[^>]+>)|([^<]+)/gi,
    (_match, openA: string | undefined, closeA: string | undefined, otherTag: string | undefined, text: string | undefined) => {
      if (openA) {
        inAnchor += 1;
        return openA;
      }
      if (closeA) {
        inAnchor = Math.max(0, inAnchor - 1);
        return closeA;
      }
      if (otherTag) return otherTag;
      if (!text || inAnchor > 0) return text ?? '';
      return transform(text);
    },
  );
}

function enhanceFaqAnswerLinks(html: string): string {
  if (!html) return '';

  return mapTextOutsideAnchors(html, (text) => {
    let next = text.replace(/\bFloor Plans\b/gi, (match) => {
      return `<a href="${FLOOR_PLANS_HREF}">${match}</a>`;
    });

    next = next.replace(
      /(occupancy qualifications\s+)(here)\b/gi,
      (_m, prefix: string, here: string) =>
        `${prefix}<a href="${OCCUPANCY_QUALIFICATIONS_HREF}" target="_blank" rel="noopener noreferrer">${here}</a>`,
    );

    return next;
  });
}

/**
 * Group FAQs by category, preserving collection order both within and across
 * groups. Items without a category collapse into a "General" bucket, but only
 * when at least one item DOES have a category — otherwise we render a single
 * flat list with no category headings.
 */
function groupByCategory(faqs: FAQItem[]): { groups: FAQGroup[]; showHeadings: boolean } {
  const anyCategorized = faqs.some((f) => hasValue(f.category));
  if (!anyCategorized) {
    return {
      groups: [
        {
          category: '',
          items: faqs.map((f, i) => ({ ...f, originalIndex: i })),
        },
      ],
      showHeadings: false,
    };
  }

  const order: string[] = [];
  const map = new Map<string, FAQGroup>();

  faqs.forEach((f, i) => {
    const cat = hasValue(f.category) ? f.category!.trim() : UNCATEGORIZED;
    if (!map.has(cat)) {
      order.push(cat);
      map.set(cat, { category: cat, items: [] });
    }
    map.get(cat)!.items.push({ ...f, originalIndex: i });
  });

  return { groups: order.map((c) => map.get(c)!), showHeadings: true };
}

// ============================================================================
// Accordion row
// ============================================================================

interface FAQRowProps {
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  panelId: string;
  buttonId: string;
}

function FAQRow({ item, isOpen, onToggle, panelId, buttonId }: FAQRowProps) {
  const question = item.question || 'Question';

  return (
    <div className={`faq-row${isOpen ? ' faq-row--open' : ''}`}>
      <h3 className="faq-row-heading">
        <button
          type="button"
          id={buttonId}
          aria-expanded={isOpen}
          aria-controls={panelId}
          onClick={onToggle}
          className="faq-row-trigger"
        >
          <span className="faq-row-question">{question}</span>
          <span className="faq-row-icon" aria-hidden="true">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        className="faq-row-panel"
        hidden={!isOpen}
      >
        <div
          className="faq-row-answer font-body text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline"
          dangerouslySetInnerHTML={{
            __html: enhanceFaqAnswerLinks(item.answer || ''),
          }}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Structured data (FAQPage JSON-LD)
// ============================================================================

function FAQStructuredData({ items }: { items: FAQItem[] }) {
  const payload = useMemo(() => {
    const mainEntity = items
      .filter((item) => (item.question || '').trim() && stripHtml(item.answer))
      .map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: stripHtml(item.answer),
        },
      }));
    if (mainEntity.length === 0) return null;
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity,
    });
  }, [items]);

  if (!payload) return null;

  return (
    <script
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: payload }}
    />
  );
}

// ============================================================================
// Main component
// ============================================================================

export function FAQList() {
  const faqs = useCollectionBinding<FAQItem>('faqlist.items', {
    collection: 'faqs',
  });

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const validItems = useMemo(
    () =>
      (faqs || []).filter(
        (item) => (item.question || '').trim() && (item.answer || '').trim(),
      ),
    [faqs],
  );

  const { groups, showHeadings } = useMemo(
    () => groupByCategory(validItems),
    [validItems],
  );

  // Keyed by original collection index so grouping doesn't invalidate open state.
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (idx: number) => {
    setOpenIndex((prev) => (prev === idx ? null : idx));
  };

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up py-12 md:py-20 bg-[var(--vibe-color-background)]"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <EditableCollectionBinding bindingPath="faqlist.items" label="FAQ Items">
          {validItems.length === 0 ? (
            <div className="text-center py-16 text-[var(--vibe-color-text-muted)]">
              <RichTextContent
                path="faqlist.empty_message"
                as="div"
                className="font-body text-base md:text-lg leading-relaxed [&_a]:text-primary [&_a]:underline"
              />
            </div>
          ) : (
            <div className="faq-groups">
              {groups.map((group) => (
                <div key={group.category || 'flat'} className="faq-group">
                  {showHeadings && group.category ? (
                    <h2 className="faq-group-heading">{group.category}</h2>
                  ) : null}
                  <div className="faq-list">
                    {group.items.map((item) => {
                      const idx = item.originalIndex;
                      const panelId = `faq-panel-${idx}`;
                      const buttonId = `faq-button-${idx}`;
                      return (
                        <FAQRow
                          key={buttonId}
                          item={item}
                          isOpen={openIndex === idx}
                          onToggle={() => handleToggle(idx)}
                          panelId={panelId}
                          buttonId={buttonId}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </EditableCollectionBinding>

        {validItems.length > 0 && <FAQStructuredData items={validItems} />}
      </div>

      <style>{`
        .faq-groups {
          display: flex;
          flex-direction: column;
          gap: 2.75rem;
        }
        .faq-group {
          display: flex;
          flex-direction: column;
        }
        .faq-group-heading {
          font-family: var(--vibe-font-heading);
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          color: var(--vibe-color-secondary);
          margin: 0 0 1rem;
          padding-bottom: 0.65rem;
          border-bottom: 1px solid var(--vibe-color-border);
        }

        .faq-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .faq-row {
          background: white;
          border: 1px solid var(--vibe-color-border);
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.2s ease, border-color 0.2s ease;
        }
        .faq-row:hover {
          border-color: var(--vibe-color-text);
        }
        .faq-row--open {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          border-color: var(--vibe-color-primary);
        }

        .faq-row-heading {
          margin: 0;
          font-size: inherit;
          font-weight: inherit;
        }

        .faq-row-trigger {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          width: 100%;
          padding: 1rem 1.25rem;
          background: transparent;
          border: none;
          text-align: left;
          cursor: pointer;
          color: var(--vibe-color-text);
          font-family: var(--vibe-font-heading);
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.4;
          letter-spacing: 0.01em;
        }
        @media (min-width: 768px) {
          .faq-row-trigger {
            padding: 1.25rem 1.5rem;
            font-size: 1.05rem;
          }
        }
        .faq-row-trigger:focus-visible {
          outline: 2px solid var(--vibe-color-primary);
          outline-offset: -2px;
        }

        .faq-row-question {
          flex: 1;
        }

        .faq-row-icon {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 1px solid var(--vibe-color-border);
          color: var(--vibe-color-text-muted);
          transition: transform 0.25s ease, color 0.2s ease, border-color 0.2s ease, background 0.2s ease;
        }
        .faq-row--open .faq-row-icon {
          transform: rotate(180deg);
          color: var(--vibe-color-text-on-primary, #fff);
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
        }

        .faq-row-panel[hidden] {
          display: none;
        }
        .faq-row-answer {
          padding: 0 1.25rem 1.25rem;
          border-top: 1px solid var(--vibe-color-border);
          padding-top: 1rem;
        }
        @media (min-width: 768px) {
          .faq-row-answer {
            padding: 1.25rem 1.5rem 1.5rem;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .faq-row,
          .faq-row-icon {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FAQList',
  displayName: 'FAQ List',
  tokens: {
    'faqlist.empty_message': {
      label: 'Empty State Message',
      type: 'richtext',
      description:
        'Shown when the FAQs collection is empty.',
      defaultValue:
        '<p>We\'re preparing answers to the questions residents ask most. In the meantime, please reach out and we\'ll be glad to help.</p>',
    },
  },
  collectionBindings: {
    'faqlist.items': {
      label: 'FAQ Items',
      collection: 'faqs',
    },
  },
};

(FAQList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FAQList;
